import React, { useState, useEffect } from 'react';
import presetApi from '@services/presetApi.jsx';
import { getTimerRedirectOptions, migrateTimerRedirectValue } from '../utils/pageNavigationUtils';

const PresetPaymentSetting = ({ preset, presetPageMasters }) => {
    const [isLoading, setIsLoading] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const presetId = searchParams.get('presetId');
    const [presetPaymentPage, setPresetPaymentPage] = useState(null);

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        timerSeconds: 10,
        timerTimeoutRedirectUrl: 'start',
        isActive: true
    });

    // 타이머 관련
    const [timer, setTimer] = useState(10);
    const [timeOverPage, setTimeOverPage] = useState('start');
    
    // 타이머 리다이렉트 옵션
    const [timerRedirectOptions, setTimerRedirectOptions] = useState([]);

    // 페이지 데이터 로드
    useEffect(() => {
        loadPaymentPageData();
    }, [presetId]);
    
    // 타이머 리다이렉트 옵션 업데이트
    useEffect(() => {
        if (presetPageMasters && presetPageMasters.length > 0) {
            const options = getTimerRedirectOptions('payment', presetPageMasters);
            setTimerRedirectOptions(options);
        }
    }, [presetPageMasters]);

    // 결제 페이지 데이터 로드
    const loadPaymentPageData = async () => {
        try {
            setIsLoading(true);
            const paymentPage = await presetApi.getPaymentPage(presetId);
            
            setPresetPaymentPage(paymentPage);
            
            // 기존 timerTimeoutRedirectUrl 값을 새로운 형식으로 마이그레이션
            const migratedRedirectValue = migrateTimerRedirectValue(
                paymentPage.timerTimeoutRedirectUrl, 
                'payment'
            );
            
            setFormData({
                timerSeconds: paymentPage.timerSeconds ?? 10,
                timerTimeoutRedirectUrl: migratedRedirectValue,
                isActive: paymentPage.isActive ?? true
            });
            
            setTimer(paymentPage.timerSeconds ?? 10);
            setTimeOverPage(migratedRedirectValue);
        } catch (error) {
            console.error('결제 페이지 데이터 로드 실패:', error);
            // 에러 시 기본값 유지
        } finally {
            setIsLoading(false);
        }
    };

    // 타이머 조절 핸들러
    const handleTimerChange = (action) => {
        let newTimer;
        if (action === 'increase') {
            newTimer = Math.min(timer + 1, 60);
        } else {
            newTimer = Math.max(timer - 1, 1);
        }
        
        setTimer(newTimer);
        setFormData({
            ...formData,
            timerSeconds: newTimer
        });
    };

    // 타임 오버 페이지 변경 핸들러
    const handleTimeOverPageChange = (e) => {
        const newPage = e.target.value;
        setTimeOverPage(newPage);
        setFormData({
            ...formData,
            timerTimeoutRedirectUrl: newPage
        });
    };

    // 저장 이벤트 리스너
    useEffect(() => {
        const eventName = `savePagePreset_payment`;

        const saveHandler = async (event) => {
            if (!preset?.id) {
                alert("프리셋 정보가 없습니다.");
                return;
            }

            try {
                setIsLoading(true);

                const dataToSave = {
                    presetId: event.detail.presetId,
                    timerSeconds: formData.timerSeconds,
                    timerTimeoutRedirectUrl: formData.timerTimeoutRedirectUrl,
                    isActive: formData.isActive
                };
                
                const response = await presetApi.savePaymentPage(event.detail.presetId, dataToSave);
                console.log("결제 페이지 저장 성공:", response);

            } catch (error) {
                console.error("결제 페이지 저장 실패:", error);
                alert("결제 페이지 저장 중 오류가 발생했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        document.addEventListener(eventName, saveHandler);

        return () => {
            document.removeEventListener(eventName, saveHandler);
        };
    }, [preset?.id, formData]);

    if (isLoading) {
        return <div className="preset-settings">로딩 중...</div>;
    }

    return (
        <div className="preset-settings">
            {/* 타이머 설정 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">타이머</h4>
                <div className="settings-row">
                    <div className="settings-label">타이머 시간</div>
                    <div className="timer-control-container">
                        <button 
                            className="timer-btn" 
                            onClick={() => handleTimerChange('decrease')}
                        >
                            <i className="ri-arrow-down-s-fill"></i>
                        </button>
                        <input 
                            type="text" 
                            className="timer-input" 
                            value={timer}
                            readOnly
                        />
                        <button 
                            className="timer-btn" 
                            onClick={() => handleTimerChange('increase')}
                        >
                            <i className="ri-arrow-up-s-fill"></i>
                        </button>
                        <span className="timer-unit">초</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">타임 오버 이동 페이지</div>
                    <div className="page-select-container">
                        <select 
                            className="page-select"
                            value={timeOverPage}
                            onChange={handleTimeOverPageChange}
                        >
                            {timerRedirectOptions.map(option => (
                                <option 
                                    key={option.value} 
                                    value={option.value}
                                    disabled={!option.available}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <i className="ri-arrow-down-s-line select-arrow"></i>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PresetPaymentSetting;
